import { useParams, Link } from "react-router-dom";
import { blogPosts } from "../data/blogPosts";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSlug from "rehype-slug";
import { useEffect, useState } from "react";

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);
  const [headings, setHeadings] = useState([]);
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    if (!post?.content) return;

    const regex = /^#{2,4}\s+(.+)$/gm;
    const matches = [...post.content.matchAll(regex)];

    const parsed = matches.map((m) => {
      const text = m[1].trim();
      const id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");
      return { text, id };
    });

    setHeadings(parsed);
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6">
        <div className="bg-white p-10 rounded-2xl shadow-lg text-center">
          <h2 className="text-2xl font-bold text-brandBlue mb-3">Post Not Found</h2>
          <Link
            to="/blog"
            className="inline-block bg-brandBlue text-white px-6 py-3 rounded-lg hover:bg-brandPink transition-colors"
          >
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Banner */}
      {post.image && (
        <div className="relative w-full max-h-[420px] overflow-hidden">
          <img
            src={post.image}
            alt={post.alt}
            className="w-full h-[420px] object-cover brightness-75"
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-white px-4">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-2">{post.title}</h1>
            <p className="text-sm md:text-base opacity-90">{post.date}</p>
          </div>
        </div>
      )}

      {/* Layout */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 px-6 md:px-10 py-12">
        {/* Table of Contents */}
        <div className="lg:sticky lg:top-20 space-y-4">
          <button
            className="bg-gradient-to-r from-brandBlue to-brandPink text-white font-semibold px-5 py-2 rounded-lg w-full hover:scale-[1.02] transition-transform"
            onClick={() => setTocOpen(!tocOpen)}
            aria-expanded={tocOpen}
          >
            ☰ {tocOpen ? "Hide Contents" : "Show Contents"}
          </button>

          <div
            className={`transition-all duration-500 ease-in-out overflow-hidden bg-white rounded-xl shadow-md ${
              tocOpen ? "max-h-[70vh] opacity-100 p-5" : "max-h-0 opacity-0 p-0"
            }`}
          >
            <h3 className="text-lg font-bold text-brandBlue mb-3">
              Table of Contents
            </h3>
            <ul className="space-y-2">
              {headings.map((h) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    className="block text-gray-700 hover:text-brandPink transition-colors text-sm"
                    onClick={() => setTocOpen(false)}
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Blog Content */}
        <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12 prose prose-lg max-w-none prose-headings:text-brandBlue prose-a:text-brandPink prose-a:no-underline hover:prose-a:text-brandBlue">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeSlug]}
          >
            {post.content}
          </ReactMarkdown>
        </article>
      </div>

      {/* Back Link */}
      <div className="text-center pb-16">
        <Link
          to="/blog"
          className="inline-block bg-brandBlue text-white px-6 py-3 rounded-lg font-medium hover:bg-brandPink transition-colors"
        >
          ← Back to Blog
        </Link>
      </div>
    </div>
  );
}
